import React, { FC, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Tooltip, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import useModuleAccess from '../../hooks/useModuleAccess';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import BadgeIcon from '@mui/icons-material/Badge';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';

interface ConfigItem {
  label: string;
  path: string;
  // registry module / sub-module the link opens (plan + role locked)
  module?: string;
  sub?: string;
}

interface ConfigCardProps {
  title: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  items: ConfigItem[];
}

const ConfigCard: FC<ConfigCardProps> = ({ title, icon, color, bgColor, items }) => {
  const navigate = useNavigate();
  const { can, denyReason } = useModuleAccess();
  return (
    <Box
      sx={{
        width: 260,
        minHeight: 280,
        background: '#fff',
        border: '2px solid #d1d5db',
        borderRadius: '16px',
        padding: '10px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: 'none',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          padding: '12px 14px',
          background: bgColor,
          borderRadius: '8px',
          mt: -0.5,
          mb: 1,
        }}
      >
        <Box sx={{ color, display: 'flex', alignItems: 'center' }}>{icon}</Box>
        <Typography variant="subtitle1" sx={{ color: '#111' }}>{title}</Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {items.map((item) => (item.module && !can(item.module, 'view', item.sub) ? (
          <Tooltip key={item.label} arrow placement='right' title={denyReason(item.module, 'view', item.sub)}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 10px', typography: 'body1', color: '#9ca3af', cursor: 'not-allowed' }}>
              {item.label}
              <LockIcon sx={{ fontSize: 14 }} />
            </Box>
          </Tooltip>
        ) : (
          <Box
            key={item.label}
            onClick={() => navigate(item.path)}
            sx={{
              padding: '5px 10px',
              typography: 'body1',
              color: '#374151',
              cursor: 'pointer',
              borderRadius: '6px',
              transition: 'background 0.15s ease',
              '&:hover': { background: '#f3f4f6', color },
            }}
          >
            {item.label}
          </Box>
        )))}
      </Box>
    </Box>
  );
};

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

const Section: FC<SectionProps> = ({ title, children }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="h5" sx={{ color: '#111', mb: 1 }}>{title}</Typography>
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>{children}</Box>
  </Box>
);

const Config: FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  // Reset scroll to the top on mount.
  useEffect(() => {
    window.scrollTo(0, 0);
    // Some layouts scroll an inner container rather than the window.
    document.querySelector('main')?.scrollTo?.(0, 0);
  }, []);

  return (
    <Box sx={{ background: '#f9fafb' }}>
      <Box sx={{ padding: '12px 24px', maxWidth: 1400, margin: '0 auto' }}>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'center' }}>
        <Section title="People Settings">
          <ConfigCard
            title="Employee"
            icon={<BadgeIcon sx={{ fontSize: 20 }} />}
            color="#3b82f6"
            bgColor="#dbeafe"
            items={[
              { label: 'Employees', path: '/people/employee', module: 'employee', sub: 'employees' },
              { label: 'Employee Invite', path: '/people/employee', module: 'employee', sub: 'employees' },
              { label: 'Roles', path: '/users', module: 'members' },
            ]}
          />
          <ConfigCard
            title="People"
            icon={<PeopleAltIcon sx={{ fontSize: 20 }} />}
            color="#10b981"
            bgColor="#d1fae5"
            items={[
              { label: 'Leave Config', path: '/people/employee-config?tab=0', module: 'people', sub: 'leave-config' },
              { label: 'Shifts', path: '/people/employee-config?tab=1', module: 'people', sub: 'shifts' },
              { label: 'Holidays', path: '/people/employee-config?tab=2', module: 'people', sub: 'holidays' },
              { label: 'Week Offs', path: '/people/employee-config?tab=3', module: 'people', sub: 'week-offs' },
              { label: 'Shift Pattern Config', path: '/people/employee-config?tab=4', module: 'people', sub: 'shift-pattern' },
              { label: 'Employment Assignment', path: '/people/employee-config?tab=5', module: 'people', sub: 'employment-assignment' },
              { label: 'Work Locations', path: '/people/employee-config?tab=6', module: 'people', sub: 'work-locations' },
            ]}
          />
        </Section>

        <Section title="Payroll Settings">
          <ConfigCard
            title="Payroll"
            icon={<AccountBalanceIcon sx={{ fontSize: 20 }} />}
            color="#f59e0b"
            bgColor="#fef3c7"
            items={[
              { label: 'Salary Heads', path: '/people/payroll-settings?tab=salaryHead', module: 'people', sub: 'salary-heads' },
              { label: 'Salary Grades', path: '/people/payroll-settings?tab=salaryGrade', module: 'people', sub: 'salary-grades' },
              { label: 'Employee Salary Config', path: '/people/payroll-settings?tab=employeeSalaryConfig', module: 'people', sub: 'employee-salary-config' },
              { label: 'Loans', path: '/people/payroll-settings?tab=loan', module: 'people', sub: 'loans' },
            ]}
          />
          <ConfigCard
            title="Salary Management"
            icon={<MonetizationOnIcon sx={{ fontSize: 20 }} />}
            color="#8b5cf6"
            bgColor="#ede9fe"
            items={[
              { label: 'Salary Management', path: '/people/salary/list', module: 'people', sub: 'salary-management' },
              { label: 'Process Salary', path: '/people/salary/list?open=process-salary', module: 'people', sub: 'salary-management' },
              { label: 'Statutory Compliance', path: '/coa/statutory-compliance', module: 'coa', sub: 'statutory-compliance' },
              { label: 'Expenses', path: '/expenses', module: 'expenses' },
            ]}
          />
        </Section>
        </Box>
      </Box>
    </Box>
  );
};

export default Config;
